import React, { useState, useEffect } from "react";
import {
	getCategories,
	getHomeLayout,
	saveHomeLayout,
} from "../utils/Api/ApiCalls";
import Swal from "sweetalert2";
import { GripVertical } from "lucide-react";

const HomeLayout = () => {
	const [layouts, setLayouts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [sections, setSections] = useState([]);
	const [categories, setCategories] = useState([]);
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [collapsedSections, setCollapsedSections] = useState([]);

	useEffect(() => {
		// Initialize newly added sections as collapsed by default for easier dragging
		setCollapsedSections((prev) =>
			sections.map((_, i) => (prev[i] !== undefined ? prev[i] : true)),
		);
	}, [sections]);

	useEffect(() => {
		const init = async () => {
			try {
				const layoutData = await getHomeLayout();

				if (layoutData?.length > 0) {
					setLayouts(layoutData[0] || []);
				} else {
					setLayouts(layoutData);
				}

				if (layoutData?.length > 0) {
					setSections(layoutData[0]?.sections || []);
				} else {
					setSections(layoutData?.sections);
				}

				const categoryData = await getCategories();
				console.log(categoryData?.categories);

				setCategories(categoryData?.categories);
			} catch (err) {
				Swal.fire({
					title: "Error loading page",
					icon: "error",
					text: "Please check your internet connection.",
				}).then(() => {
					// window.location.reload();
				});
			} finally {
				setLoading(false);
			}
		};

		init();
	}, []);

	const handleDragStart = (e, index) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e, index) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleDrop = (e, dropIndex) => {
		e.preventDefault();

		if (draggedIndex === null || draggedIndex === dropIndex) return;

		const updated = [...sections];
		const [draggedItem] = updated.splice(draggedIndex, 1);
		updated.splice(dropIndex, 0, draggedItem);

		setSections(updated);
		setDraggedIndex(null);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const handleSectionChange = (index, field, value) => {
		const updated = [...sections];
		updated[index][field] = value;
		if (field === "displayType") {
			updated[index].items = [""];
		}
		setSections(updated);
	};

	const handleItemChange = (sectionIndex, itemIndex, value) => {
		const updated = [...sections];
		updated[sectionIndex].items[itemIndex] = value;
		setSections(updated);
	};

	const addSection = () => {
		setSections([
			...sections,
			{ title: "", displayType: "category", items: [""] },
		]);
	};

	const removeSection = (index) => {
		const updated = [...sections];
		updated.splice(index, 1);
		setSections(updated);
	};

	const addItem = (sectionIndex) => {
		const updated = [...sections];
		updated[sectionIndex].items.push("");
		setSections(updated);
	};

	const removeItem = (sectionIndex, itemIndex) => {
		const updated = [...sections];
		updated[sectionIndex].items.splice(itemIndex, 1);
		setSections(updated);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		try {
			console.log("Layout :", layouts);
			await saveHomeLayout(layouts.id, sections);
			Swal.fire({
				title: "Layout Updated!",
				icon: "success",
			}).then(() => {
				// window.location.reload();
			});
		} catch (err) {
			Swal.fire({
				title: "Error saving layout.",
				icon: "error",
				text: "Please try again.",
			}).then(() => {
				// window.location.reload();
			});
			console.error("Save failed:", err);
		}
	};

	if (loading) return <div className="text-center p-4">Loading layouts...</div>;

	return (
		<div className="p-5 max-w-4xl mx-auto">
			<h1 className="text-2xl font-bold mb-6">Home Layout Editor</h1>

			{layouts.length > 0 && (
				<div className="mb-4 text-gray-600">
					Last Updated: {new Date(layouts?.updatedAt).toLocaleString()}
				</div>
			)}

			{!sections ? (
				<div>No Layout to display.</div>
			) : (
				sections?.map((section, sIndex) => (
					<div
						key={sIndex}
						draggable
						onDragStart={(e) => handleDragStart(e, sIndex)}
						onDragOver={(e) => handleDragOver(e, sIndex)}
						onDrop={(e) => handleDrop(e, sIndex)}
						onDragEnd={handleDragEnd}
						className={`border p-2 my-2 rounded bg-white transition-all ${
							draggedIndex === sIndex
								? "opacity-50 border-blue-400"
								: "border-gray-200"
						} hover:border-gray-300 cursor-move`}
						style={{ maxWidth: "600px" }}
					>
						<div className="flex items-center justify-between gap-2 mb-2">
							<div className="flex items-center gap-2">
								<span
									className="text-gray-400 select-none cursor-move"
									aria-hidden
								>
									⋮⋮
								</span>
								<div className="font-bold text-lg">Section {sIndex + 1}</div>
								<span className="text-xs text-gray-500">
									{sections[sIndex]?.title || "Untitled"} •{" "}
									{sections[sIndex]?.displayType}
								</span>
							</div>
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setCollapsedSections((prev) => {
										const next = [...prev];
										next[sIndex] = !next[sIndex];
										return next;
									});
								}}
								className="px-2 py-1 text-sm rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer"
							>
								{collapsedSections[sIndex] ? "Expand" : "Collapse"}
							</button>
						</div>

						{!collapsedSections[sIndex] && (
							<>
								<div className="mb-2">
									<label className="block font-medium mb-1">Title:</label>
									<input
										type="text"
										value={section.title}
										onChange={(e) =>
											handleSectionChange(sIndex, "title", e.target.value)
										}
										className="w-full border border-gray-300 p-2 rounded"
									/>
								</div>

								<div className="mb-2">
									<label className="block font-medium mb-1">
										Display Type:
									</label>
									<select
										value={section.displayType}
										onChange={(e) =>
											handleSectionChange(sIndex, "displayType", e.target.value)
										}
										className="w-full border border-gray-300 p-2 rounded"
									>
										<option value="category">Category</option>
										<option value="label">Label</option>
									</select>
								</div>

								<div className="mb-2">
									<label className="block font-medium mb-2">
										Items: Choose Category or type in Label
									</label>
									{section.items &&
										section.items.map((item, iIndex) => (
											<div
												key={iIndex}
												className="flex gap-2 mb-2 items-center"
											>
												{section.displayType === "category" ? (
													<select
														value={item}
														onChange={(e) =>
															handleItemChange(sIndex, iIndex, e.target.value)
														}
														className="flex-1 border border-gray-300 p-2 rounded"
													>
														<option value="">-- Select Category --</option>
														{categories &&
															categories.map((cat) => (
																<option key={cat?.ID} value={cat?.ID}>
																	{cat?.categoryName}
																</option>
															))}
													</select>
												) : (
													<input
														type="text"
														value={item}
														onChange={(e) =>
															handleItemChange(sIndex, iIndex, e.target.value)
														}
														className="flex-1 border border-gray-300 p-2 rounded"
													/>
												)}

												{iIndex !== 0 && (
													<button
														type="button"
														onClick={() => removeItem(sIndex, iIndex)}
														className="bg-red-100 text-red-600 px-3 py-2 rounded hover:bg-red-200"
													>
														Remove
													</button>
												)}
											</div>
										))}
									{section.displayType !== "category" && (
										<button
											type="button"
											onClick={() => addItem(sIndex)}
											className="mt-2 bg-blue-100 text-blue-600 px-3 py-2 rounded hover:bg-blue-200"
										>
											+ Add Item
										</button>
									)}
								</div>

								<button
									type="button"
									onClick={() => removeSection(sIndex)}
									className="bg-red-200 text-red-800 px-3 py-2 rounded hover:bg-red-300"
								>
									Remove Section
								</button>
							</>
						)}
					</div>
				))
			)}

			<div className="flex gap-4 mt-6">
				<button
					onClick={addSection}
					className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
				>
					+ Add Section
				</button>

				<button
					onClick={handleSave}
					className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
				>
					Save Layout
				</button>
			</div>
		</div>
	);
};

export default HomeLayout;
